import { SearchOptions, SearchParameters } from '../components/rentware-search/utils';
import {
  Price,
  PriceCalculation,
  SplitOptionType,
  StepErrorType,
  TotalPriceCalculation,
  WidgetType,
  SortByOptions,
  SortDirOptions,
  SelectOption,
  MileageOption,
  Filters,
  AmountToPay,
} from '../types/types';
import { CodeStatus, PaymentOption } from '../components/rtr-payment/utils/types';
import { ArticleLocation } from '../components/rtr-article-details/utils/types';
import { WidgetConfig } from '../types/config';
import { ErrorType } from '../components/rtr-errors/utils';

export interface App {
  initialized: boolean;
  bookingId: string;
  bookingCompleted: boolean;
  bookingExpired: boolean;
  customer: any;
  expandCart: boolean;
  loadedComponents: { [name: string]: boolean };
  loadedSteps: { [name: string]: boolean };
  locale: string;
  configLocale: string;
  systemDefaultLanguage: string;
  systemLanguages: [];
  showCheckout: boolean;
  showProcessingSpinner: boolean;
  processingSpinnerContent?: ProcessingSpinnerContent;
  showDetailsForId: string;
  showGalleryForId: string;
  showReservationForId: string;
  showDetailsToCalendar: boolean;
  currentStep: StepItem;
  leadStep: StepItem;
  paymentFailed: boolean;
  paymentMethod: PaymentMethod;
  recurringPaymentMode: boolean;
  paymentMethodRecurring: boolean;
  secondaryPaymentMethod: any;
  prepayStatus: CodeStatus;
  paymentSplitOption: SplitOption;
  amountToPay: AmountToPay;
  billingStepValues: any;
  accessToken: string;
  appLoaded: boolean;
  currency: string;
  navigateToStepInProgress: boolean;
  dataFormFetchingInProgress: boolean;
  dataFormSendingInProgress: boolean;
}

export interface ProcessingSpinnerContent {
  text?: string;
  text1?: string;
  text2?: string;
}

export interface PaymentMethod {
  value: string;
  label: string;
}

export interface System {
  company: string;
  defaultLanguage: string;
  formConfig: FormConfig;
  locations: any;
  translations: Translations;
  configTranslations: Translations;
  currency: string;
  currencyCode: string;
  pricing: SystemPricing;
  widgetConfig: WidgetConfig;
  unitsConfig: UnitConfig;
  systemLoaded: boolean;
}

export interface SystemPricing {
  itemPriceCalculation: string;
  priceDisplay: string;
  format: SystemPricingFormat;
}

export interface SystemPricingFormat {
  decimalSeparator: string;
  currencyPosition: string;
}

export interface WidgetsCustomization {
  fontFamily: 'inherit' | string;
  buttonColor: string;
  buttonFontColor: string;
  cartButtonPosition: string;
  cartIcon: string;
  cartButtonColor: string;
  cartButtonFontColor: string;
  checkoutFontColor: string;
  checkoutBackgroundColor: string;
  statusBarBackgroundColor: string;
  articleBookingBackgroundColor: string;
  articleBookingBorderColor: string;
}

export interface PaymentConfig {
  hasVouchers: boolean;
  methods: string[];
  methodsRecurring: string[];
  prePaymentRecurring: number;
  stripeApiKey: string;
  splitOptions: SplitOptions;
  prepaymentPasscodeEnabled: boolean;
  giftCard: GiftCard;
  duePayments: DuePayment[];
  paypalClientId: string;
}

export enum ErrorLayout {
  Error = 'error',
  Success = 'success',
}

export interface RtrError {
  message: string;
  type?: ErrorType; // Used for elements to identify if connected with error
  layout?: ErrorLayout;
  errorKey?: string; // Used for identify instance of error
  errorTimer?: any;
}

export interface SplitOptions {
  default: SplitOption;
  optional: SplitOption[];
  byMethod: ByMethodSplitOptions[];
}

export interface SplitOption {
  value: number;
  type: SplitOptionType;
  excludeDeposit: boolean;
  extraCartItemId: string;
}

export interface ByMethodSplitOptions {
  paymentMethod: string;
  default: SplitOption;
  optional: SplitOption[];
}

export interface DuePayment {
  date: string;
  amount: number;
  daysOfUsage: number;
}

export interface Widget {
  kind: 'date' | 'dateRange' | 'dateTimeRangeCombo';
  people: boolean;
  locationEnabled: boolean;
  timePickerEnabled: boolean;
  locationEmptyByDefault: boolean;
  filterByTags: Tags;
  timePicker: WidgetTimePicker;
}

export interface WidgetTimePicker {
  durations?: TimePickerDurations[];
  hours?: TimePickerHours[];
  widget: WidgetType;
}

export interface TimeSlotPicker {
  options?: SelectOption[];
  selectedOption?: string;
  onOptionChange?: (string) => void;
  pickerType?: WidgetType;
}

export interface PickerPeriod {
  slug: string;
  label: string;
}

export interface TimePickerDurations extends PickerPeriod {
  value: string;
  unit: string;
}

export interface TimePickerHours extends PickerPeriod {
  from: string;
  to: string;
}

export interface Tags {
  required: boolean;
  label: string;
  tags: string[];
}

export interface FormConfig {
  address: BillingAddress;
  shippingAddress: ShippingAddress;
  shippingAddressSameAsAddress: boolean;
}

export interface Translations {
  [code: string]: TranslationEntries;
}

export interface TranslationEntries {
  countries: CountryOption[];
  legal: any;
}

export interface CountryOption {
  [code: string]: string;
}

export enum FieldRequirement {
  Required = 'required',
  Optional = 'optional',
  Hide = 'hide',
}

export enum OrderKind {
  Rent = 'rent',
  Sale = 'sale',
}

export interface Address {
  addressTwo: FieldRequirement;
  city: FieldRequirement;
  company: FieldRequirement;
  country: FieldRequirement;
  firstName: FieldRequirement;
  lastName: FieldRequirement;
  phone: FieldRequirement;
  street: FieldRequirement;
  zip: FieldRequirement;
}

export interface BillingAddress extends Address {
  email: string;
  taxId: string;
}

export interface ShippingAddress extends Address {}

export interface Cart {
  items: CartItem[];
  price: Price;
  discountedPrice: Price;
  discountedPriceWithoutDeposit?: Price;
  discountedPricePerMonthWithoutDeposit?: Price;
  depositPrice: Price;
  totalQuantity: number;
  totalQuantityWithShipping: number;
  groupedQuantityWithShipping: number;
  voucher: Voucher;
  shipping: ShippingItem;
  totalPriceCalculation: TotalPriceCalculation;
}

export interface Search {
  showFilters: boolean;
  searchParameters: SearchParameters;
  searchOptions: SearchOptions;
  shownFilters: Filters[];
  waitingForUpdate: boolean;
  allTags: Tags;
}

export interface Voucher {
  code: string;
  title: string;
  amount: VoucherAmount;
}

export interface VoucherAmount {
  unit: string;
  value: number;
}

export interface GiftCard {
  code: string;
  initialAmount: number;
  usedAmount: number;
  remainingAmount: number;
}

export interface ShippingItem {
  optionId: string;
  method: string;
  name: string;
  price: Price;
  priceType: ShippingPriceType;
}

export enum ShippingPriceType {
  Fixed = 'fixed',
  Distance = 'distance',
}

export interface ConnectedArticle extends CartItem {
  connectedAmount: number;
}

export interface CartItem {
  id: string;
  itemId: string;
  name: string;
  qty: number;
  minQty: number;
  maxQty: number;
  availableQty: number;
  image: string;
  title?: string;
  removable: boolean;
  discountedPrice: Price;
  individualDiscountedPrice: Price;
  products: CartItemProduct[];
  isDeposit: boolean;
  isMileageOption: boolean;
  isGiftCard: boolean;
  mileageOption: MileageOption;
  availableMileageOptions: MileageOption[];
  priceCalculationType: PriceCalculation;
  parentIds: string[];
}

export interface CartItemProduct {
  id: string;
  name: string;
  variation: Variation;
  quantity: number;
}

export interface BookingDetailsDate {
  fromDate?: Date;
  fromTime?: string;
  toDate?: Date;
  toTime?: string;
  selectedTimeSlot?: string;
}

export interface BookingDetailsItemMileage {
  [itemId: string]: string;
}

export interface BookingDetailsItemVariation {
  [itemId: string]: string;
}

export interface BookingDetails extends BookingDetailsDate {
  selectedMileageIds?: { BookingDetailsItemMileage? };
  selectedVariationIds: { BookingDetailsItemVariation? };
  selectedLocationId: string;
  peopleNumber: number;
  orderDetails: OrderDetails;
  cart: Cart;
}

export interface OrderDetails {
  id: string;
  kind: OrderKind;
  fullDayIntent: boolean;
  fromDate: string;
  toDate: string;
  expiresAt: number;
  isCompleted: boolean;
  isExpired: boolean;
  people?: number;
  location?: {
    id: string;
    label: string;
  };
  timeSlotIntent: string;
}

export interface StepItem {
  id: string;
  name?: string;
  description?: string;
  kind?: string;
  slug?: string;
  isValid?: boolean;
  tabs?: Tab[];
  dataForm?: DataForm;
  skipStep?: boolean;
  stepNumber: number;
  stepsTotal: number;
  visitContext: string;
  visited: boolean;
}

export interface DataFormConfig {
  dataFormId: string;
}

export interface DataForm {
  id: string;
  accountId: string;
  title: string;
  label: string;
  description: string;
  fields: DataFormField[];
}

export interface Shipping {
  options: ShippingOption[];
  selectedMethodId: string;
  shippingAddress?: string;
}

export interface ShippingOption {
  id: string;
  method: string;
  name: string;
  grossPrice: number;
  priceType: 'distance' | 'fixed' | 'weight';
  distance: number;
}

export interface StepOffers {
  tabs: Tab[];
  isLoading: boolean;
}

export interface Tab {
  id: string;
  name: string;
  items: StepOffer[];
}

export interface DataFormField {
  id: string;
  dataFormId: string;
  label: string;
  type: string;
  isRequired: boolean;
  readOnly: boolean;
  options: string[];
  optionsString: string;
  position: number;
}

export interface StepOffer {
  id: string;
  orderItemId: string;
  name: string;
  title: string;
  description: string;
  image: string;

  quantityInCart: number;
  quantity: number;
  minQuantity: number;
  maxQuantity: number;

  netPrice: number;
  grossPrice: number;
  priceCalculationType: PriceCalculation;

  mileageOptions?: MileageOption[];
  mileageIdInCart: string;
  variation: Variation;
}

export interface Variation {
  id: string;
  name: string;
  items: VariationItem[];
}

export interface VariationItem {
  id: string;
  name: string;
  quantity?: number;
  quantityInCart?: number;
  maxQuantity?: number;
}

export interface VariationItemWithControls extends VariationItem {
  controlValues: VariationControls;
}

export interface VariationControls {
  disabled: boolean;
  id: string;
  minAmount: number;
  maxAmount: number;
  itemAmount: number;
  changeAmountCallback: (newAmount: number, oldAmount: number) => void;
  deleteArticleCallback: () => void;
}

export interface Term {
  id: string;
  type: string;
  destination: string;
}

export interface Order {
  // account: any;
  reference: string;
  publicHash: string;
  customer: {
    email: string;
    firstName: string;
    lastName: string;
  };
  customerAddress: {
    country: string;
  };
  location: {
    id: string;
    label: string;
    street: string;
    houseNumber: string;
    addressTwo: string;
    city: string;
    zipCode: string;
    country: string;
    phoneNumber: string;
    email: string;
    companyName: string;
    longitude: number;
    latitude: number;
  };
  cart: Cart;
  // people: number;
  fullDayIntent: boolean;
  createdDate: string;
  fromDate: string;
  toDate: string;
  // lang: string;
  payments: Payment[];
  totalPaid: number;
  totalOpen: number;
  totalOpenWithoutDeposit: number;
  isOrderPayable: boolean;
  settings: {
    showCustomerData: boolean;
    showLocationPanel: boolean;
    paymentWithoutDeposit: boolean;
  };
  qrImage: string;
  status: OrderStatus;
}

export interface Payment {
  id: string;
  method: PaymentOption;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
}

export interface ItemDetails {
  id: string;
  orderItemId: string;
  name: string;
  title: string;
  description: string;
  image: string;
  images: string[];
  location: ArticleLocation;
  allowedLocations: ArticleLocation[];
  available?: boolean;
  quantity: number;
  quantityInCart?: number;
  minQuantity: number;
  maxQuantity: number;
  grossPrice: number;
  netPrice: number;
  priceCalculationType?: PriceCalculation;

  mileageOptions?: MileageOption[];
  mileageIdInCart?: string;
  variation: Variation;
}

export interface StepsError {
  stepId: string;
  type: StepErrorType;
  message?: string;
}

export interface Success {
  customer: SuccessCustomer;
  order: SuccessOrder;
}

export interface SuccessCustomer {
  firstName: string;
  email: string;
}

export interface SuccessOrder {
  reference: string;
  fromDate: string;
  toDate: string;
  detailsPageUrl: string;
}

export interface PriceOptions {
  currency: string;
  decimalSeparator: string;
  currencyPosition: string;
  displayNet: boolean;
  priceCalculation: PriceCalculation;
}

export interface UnitConfig {
  currency: string;
}

export interface GiftCardConfig {
  name: string;
  title: string;
  description: string;
}

export interface SearchParams {
  tabId: string;
  groups?: string[];
  fromDate: string;
  toDate: string;
  people: number;
  locationId: string;
  withDate: boolean;
  withTime: boolean;
  filterByTags: string[];
  filterByTagsMode: string;
  limit: number;
  offset: number;
  sortBy: SortByOptions;
  sortDir: SortDirOptions;
  timeSlot?: string;
  orderReference: string;
}

export interface CodePart {
  innerTexts?: string[];
  tagName?: string;
  tagAttributes?: CodeAttribute[];
}

export interface CodeAttribute {
  name: string;
  value: string;
}

export enum OrderStatus {
  Unknown = 'unknown',
  Cart = 'cart',
  PendingPrepayment = 'pending-prepayment',
  Reserved = 'reserved',
  Booked = 'booked',
  Cancelled = 'cancelled',
  Serving = 'serving',
  Completed = 'completed',
  Offer = 'offer',
  Internal = 'internal',
  Invoice = 'invoice',
}

export interface SingleDaySlots {
  systemSlots : any[],
  recommendedSlots : any[]
}
